import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MobileDestinationChips from "../components/MobileDestinationChips";
import MobilePropertyCard from "../components/MobilePropertyCard";
import MobileSearchCard from "../components/MobileSearchCard";
import MobileLayout from "../layouts/MobileLayout";
import useMobileProperties from "../hooks/useMobileProperties";

export default function MobileStays() {
  const loadMoreRef = useRef(null);
  const lastAutoLoadPageRef = useRef(0);
  const [params] = useSearchParams();
  const [sort, setSort] = useState("recommended");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ minPrice: "", maxPrice: "", type: "", ratingMin: "", beds: "" });
  const cityParam = params.get("city") || "";
  const location = params.get("destination") || params.get("location") || params.get("search") || cityParam || "";
  const locationType = params.get("location_type") || params.get("locationType") || "";
  const adults = params.get("adults") || params.get("guests") || "";
  const children = params.get("children") || "";
  const checkIn = params.get("checkIn") || params.get("checkin") || "";
  const checkOut = params.get("checkOut") || params.get("checkout") || "";
  const queryFilters = useMemo(
    () => ({
      destination: location,
      location,
      city: cityParam,
      location_type: locationType,
      adults,
      children,
      checkIn,
      checkOut,
      sort: sort === "recommended" ? "" : sort,
      ...filters,
    }),
    [adults, checkIn, checkOut, children, cityParam, filters, location, locationType, sort]
  );
  const { items, total, page, loading, loadingMore, error, hasMore, loadMore } = useMobileProperties(queryFilters);
  const queryKey = useMemo(() => JSON.stringify(queryFilters), [queryFilters]);
  const sorted = useMemo(() => {
    return [...items];
  }, [items]);

  useEffect(() => {
    lastAutoLoadPageRef.current = 0;
  }, [queryKey]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && lastAutoLoadPageRef.current !== page) {
          lastAutoLoadPageRef.current = page;
          loadMore();
        }
      },
      { rootMargin: "360px 0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore, loadingMore, page, sorted.length]);

  return (
    <MobileLayout>
      <div className="hv-sticky-summary">
        <div>
          <strong>{location || "All luxury stays"}</strong>
          <div className="hv-muted-row">{loading ? "Loading..." : `${total} stays available`}</div>
        </div>
        <button className="hv-secondary-btn" type="button" style={{ minHeight: 44 }} onClick={() => setFiltersOpen(true)}><i className="bi bi-sliders" /> Filters</button>
      </div>
      <MobileSearchCard compact onOpenFilters={() => setFiltersOpen(true)} />
      <section className="hv-section">
        <MobileDestinationChips />
      </section>
      <div className="hv-section-head" style={{ marginTop: 16 }}>
        <h1 className="hv-section-title">Stays</h1>
        <select className="hv-sort-select" aria-label="Sort stays" value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="recommended">Sort</option>
          <option value="price_asc">Price low to high</option>
          <option value="price_desc">Price high to low</option>
          <option value="rating">Top rated</option>
        </select>
      </div>
      {error ? <div className="hv-card" style={{ padding: 12, color: "var(--hv-maroon)" }}>{error}</div> : null}
      <div className="hv-list-stack">
        {!loading && !error && !sorted.length ? <div className="hv-mobile-empty"><h2>No stays found</h2><p>Try changing the destination or filters.</p></div> : null}
        {sorted.map((property, index) => (
          <MobilePropertyCard key={property.id} property={property} listing priority={index < 2} />
        ))}
      </div>
      {!loading && !error && sorted.length ? (
        <div className="hv-mobile-load-more" ref={loadMoreRef}>
          <div className="hv-muted-row">Showing {sorted.length} of {total} stays</div>
          {loadingMore ? <div className="hv-mobile-inline-loading" role="status">Loading more stays...</div> : null}
        </div>
      ) : null}
      {filtersOpen ? <div className="hv-mobile-filter-screen" role="dialog" aria-modal="true" aria-labelledby="mobile-filter-title">
        <header><button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><i className="bi bi-x-lg" /></button><h2 id="mobile-filter-title">Filters</h2><button type="button" onClick={() => setFilters({ minPrice: "", maxPrice: "", type: "", ratingMin: "", beds: "" })}>Clear all</button></header>
        <div className="hv-mobile-filter-body">
          <fieldset><legend>Price per night</legend><div className="hv-mini-grid"><label>Minimum<input type="number" min="0" inputMode="numeric" value={filters.minPrice} onChange={(e)=>setFilters({...filters,minPrice:e.target.value})}/></label><label>Maximum<input type="number" min="0" inputMode="numeric" value={filters.maxPrice} onChange={(e)=>setFilters({...filters,maxPrice:e.target.value})}/></label></div></fieldset>
          <fieldset><legend>Property type</legend><select value={filters.type} onChange={(e)=>setFilters({...filters,type:e.target.value})}><option value="">All types</option><option value="villa">Villa</option><option value="hotel">Hotel</option><option value="cottage">Cottage</option><option value="homestay">Homestay</option><option value="private_villa">Private villa</option></select></fieldset>
          <fieldset><legend>Bedrooms</legend><select value={filters.beds} onChange={(e)=>setFilters({...filters,beds:e.target.value})}><option value="">Any</option>{[1,2,3,4,5].map(n=><option key={n} value={n}>{n}+ bedrooms</option>)}</select></fieldset>
          <fieldset><legend>Rating</legend><select value={filters.ratingMin} onChange={(e)=>setFilters({...filters,ratingMin:e.target.value})}><option value="">Any rating</option><option value="4">4+ rating</option><option value="3">3+ rating</option></select></fieldset>
        </div>
        <footer><button className="hv-primary-btn" type="button" onClick={() => setFiltersOpen(false)}>Show {total} stays</button></footer>
      </div> : null}
    </MobileLayout>
  );
}
